import { differenceInYears } from 'date-fns'

import { parseRegion, type Region } from '#dictionary/regions'
import {
  DISTRIBUTION_BY_AGE,
  getRequiredDistributionAge,
  type RequiredDistribution,
} from '#dictionary/requiredDistributions'
import type { StandardDeductionMember } from '#dictionary/standardDeductions/getStandardDeductionGroup'

import { parseZipCode } from '../zipCode'
import type { Member as MemberType } from './schemas'

// Later it may be extended to have:
// 'parent', 'sibling', 'unmarried partner', 'grandchild', 'great-child', 'heir'
type Kind = 'person' | 'partner' | 'child' | 'dependent'

class MemberService implements StandardDeductionMember {
  public readonly id: string
  public readonly name: string | undefined
  public readonly birthday: Date
  public readonly kind: Kind
  // It is not readonly for sake of testing in playground
  public retirementAge: number
  public readonly region: Region

  public readonly isBlind: boolean = false
  // DK: it is not readonly for sake of testing in playground
  public socialSecurityAge: number | null = null

  public static readonly lifeExpectancy = 100

  constructor(member: MemberType, id: string, kind: Kind) {
    const { name, birthday, retirementAge, socialSecurityAge, zipCode } = member
    this.id = id
    this.kind = kind
    this.name = name
    // TODO: #could convert timestamp to date in the zod firebase codec instead of here
    this.birthday = birthday.toDate()
    this.retirementAge = retirementAge
    this.socialSecurityAge = socialSecurityAge ?? null
    const region = parseRegion(parseZipCode(zipCode)?.state)
    if (!region) {
      // Data-integrity failure — the persisted zipCode doesn't resolve to a
      // known US state. Downstream calculators require a valid Region; a
      // silent null here would mask the bug. Sentry picks this up via the
      // tanstack-query onError path. UX-level graceful handling tracked in
      // ZL-1021 (modal / redirect to update the zipCode).
      throw new Error(
        `MemberService: cannot resolve region from zipCode "${zipCode}" for member "${id}". ` +
          `Expected a valid US zipCode. See ZL-1021 for planned UI handling.`
      )
    }
    this.region = region
  }

  get currentAge(): number {
    return differenceInYears(new Date(), this.birthday)
  }

  // TODO: #should be moved to the engine
  getRequiredDistribution(year: number): RequiredDistribution | null {
    const memberAgeInYear = this.getAgeInTheEndOfYear(year)

    if (memberAgeInYear < getRequiredDistributionAge(this.birthday)) {
      return null
    }

    return DISTRIBUTION_BY_AGE.get(memberAgeInYear) ?? null
  }

  isQualifiedForChildTaxCredit(year: number): boolean {
    return this.kind === 'child' && this.isUnder17(year)
  }

  getAgeInTheEndOfYear(year: number): number {
    const yearLastDayDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59))
    return differenceInYears(yearLastDayDate, this.birthday)
  }

  getYearForAge(age: number): number {
    return this.birthday.getFullYear() + age
  }

  private isUnder17(year: number): boolean {
    return this.getAgeInTheEndOfYear(year) < 17
  }
}

export default MemberService
